import React, { useEffect, useState, useCallback } from 'react';
import { Search, Edit2, Package, Send, CheckCircle, X } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { supplierAPI } from '../lib/api';
import { toast } from 'sonner';
import { formatPrice } from '../lib/utils';

const CATEGORIES = [
    { id: 'cat-fruits', name: 'Fruits' },
    { id: 'cat-legumes', name: 'Légumes' },
    { id: 'cat-bio', name: 'Bio' },
    { id: 'cat-plantes', name: 'Plantes & Fleurs' },
    { id: 'cat-african', name: 'Produits Africains' },
    { id: 'cat-cosmetics', name: 'Cosmétiques' },
];

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

export default function SupplierProducts() {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [editProduct, setEditProduct] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const loadProducts = useCallback(async () => {
        try {
            const res = await supplierAPI.getProducts();
            setProducts(res.data.products || []);
        } catch {
            toast.error('Erreur chargement produits');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadProducts(); }, [loadProducts]);

    const filtered = products.filter(p =>
        p.name?.toLowerCase().includes(search.toLowerCase()) ||
        p.product_code?.toLowerCase().includes(search.toLowerCase())
    );

    const updatedCount = products.filter(p => p.supplier_update_pending && !p.supplier_update_submitted).length;

    const openEdit = (product) => {
        setEditProduct(product);
        // Prefill from pending_changes if present (so supplier sees their unvalidated edits),
        // otherwise from current live values.
        const pc = product.pending_changes || {};
        const get = (field, fallback = '') => (pc[field] !== undefined ? pc[field] : (product[field] ?? fallback));
        setEditData({
            description: get('description', ''),
            calibre: get('calibre', ''),
            price: get('price', ''),
            origin: get('origin', ''),
            stock: get('stock', ''),
            min_order: get('min_order', ''),
            contenance: get('contenance', 1),
            season_start: get('season_start', ''),
            season_end: get('season_end', ''),
        });
    };

    const handleSave = async () => {
        if (!editProduct) return;
        setSaving(true);
        try {
            const payload = {};
            if (editData.description !== '') payload.description = editData.description;
            if (editData.calibre !== '') payload.calibre = editData.calibre;
            if (editData.price !== '') payload.price = parseFloat(editData.price);
            if (editData.origin !== '') payload.origin = editData.origin;
            if (editData.stock !== '') payload.stock = parseInt(editData.stock);
            if (editData.min_order !== '') payload.min_order = parseInt(editData.min_order);
            if (editData.contenance !== '') payload.contenance = parseFloat(editData.contenance);
            if (editData.season_start !== '') payload.season_start = parseInt(editData.season_start);
            if (editData.season_end !== '') payload.season_end = parseInt(editData.season_end);

            await supplierAPI.updateProduct(editProduct.id, payload);
            toast.success(`${editProduct.name} mis à jour`);
            setEditProduct(null);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur mise à jour');
        } finally {
            setSaving(false);
        }
    };

    const handleSubmitUpdates = async () => {
        setSubmitting(true);
        try {
            const res = await supplierAPI.validateUpdates();
            toast.success(res.data.message);
            loadProducts();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur lors de la soumission');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="p-6 space-y-4">
                {[1, 2, 3].map(i => <div key={i} className="h-16 bg-muted animate-pulse rounded-xl" />)}
            </div>
        );
    }

    return (
        <div className="p-4 md:p-6 space-y-4" data-testid="supplier-products-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <Package className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Mes Produits</h1>
                        <p className="text-sm text-muted-foreground">{products.length} produits</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    {updatedCount > 0 && (
                        <Button
                            onClick={handleSubmitUpdates}
                            disabled={submitting}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl"
                            data-testid="submit-updates-btn"
                        >
                            <Send className="w-4 h-4 mr-2" />
                            {submitting ? 'Envoi...' : `Valider et envoyer (${updatedCount})`}
                        </Button>
                    )}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder="Rechercher..."
                            className="pl-9 w-56 rounded-xl"
                            data-testid="search-products"
                        />
                    </div>
                </div>
            </div>

            <Card className="rounded-xl">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead className="w-8"></TableHead>
                                    <TableHead className="min-w-[110px]">Code</TableHead>
                                    <TableHead className="min-w-[180px]">Désignation</TableHead>
                                    <TableHead className="min-w-[80px]">Contenance</TableHead>
                                    <TableHead className="min-w-[200px]">Description</TableHead>
                                    <TableHead className="min-w-[90px]">Origine</TableHead>
                                    <TableHead className="min-w-[90px]">Prix unitaire</TableHead>
                                    <TableHead className="min-w-[60px]">Stock</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filtered.map(product => {
                                    const isUpdated = product.supplier_update_pending && !product.supplier_update_submitted;
                                    return (
                                        <TableRow
                                            key={product.id}
                                            className={`cursor-pointer hover:bg-emerald-50/40 transition-colors ${isUpdated ? 'bg-emerald-50/60' : ''}`}
                                            onClick={() => openEdit(product)}
                                            data-testid={`product-row-${product.id}`}
                                        >
                                            <TableCell>
                                                {isUpdated && (
                                                    <div className="w-6 h-6 rounded-full bg-emerald-500 flex items-center justify-center" data-testid={`updated-badge-${product.id}`}>
                                                        <CheckCircle className="w-4 h-4 text-white" />
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">{product.product_code}</TableCell>
                                            <TableCell className="font-medium">
                                                <div className="flex items-center gap-2">
                                                    {product.image_url && <img src={product.image_url} alt="" className="w-8 h-8 rounded object-cover" />}
                                                    {product.name}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-sm">{product.contenance || 1}</TableCell>
                                            <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{product.description || '-'}</TableCell>
                                            <TableCell className="text-sm">{product.origin || '-'}</TableCell>
                                            <TableCell className="font-semibold text-emerald-700">{formatPrice(product.price)}</TableCell>
                                            <TableCell className="text-sm">{product.stock ?? '-'}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Edit Dialog - Full product detail */}
            <Dialog open={!!editProduct} onOpenChange={(v) => !v && setEditProduct(null)}>
                <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-product-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-emerald-800 flex items-center gap-2">
                            <Edit2 className="w-5 h-5" />Fiche Produit — {editProduct?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl">
                            {editProduct?.image_url && (
                                <img src={editProduct.image_url} alt={editProduct.name} className="w-20 h-20 object-cover rounded-xl border" />
                            )}
                            <div>
                                <p className="font-mono text-sm font-medium text-emerald-700">{editProduct?.product_code}</p>
                                <p className="text-lg font-bold">{editProduct?.name}</p>
                                <p className="text-xs text-muted-foreground">{CATEGORIES.find(c => c.id === editProduct?.category_id)?.name}</p>
                            </div>
                        </div>

                        <div>
                            <Label className="text-xs font-semibold">Description</Label>
                            <textarea value={editData.description} onChange={e => setEditData(d => ({ ...d, description: e.target.value }))}
                                className="w-full h-20 rounded-md border border-input bg-background px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-emerald-500"
                                placeholder="Description du produit..." data-testid="edit-description" />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="text-xs font-semibold">Contenance</Label>
                                <Input type="number" step="0.01" min="0" value={editData.contenance} onChange={e => setEditData(d => ({ ...d, contenance: e.target.value }))} data-testid="edit-contenance" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Calibre</Label>
                                <Input value={editData.calibre} onChange={e => setEditData(d => ({ ...d, calibre: e.target.value }))} data-testid="edit-calibre" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Prix HT (EUR)</Label>
                                <Input type="number" step="0.01" value={editData.price} onChange={e => setEditData(d => ({ ...d, price: e.target.value }))} data-testid="edit-price" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Origine</Label>
                                <Input value={editData.origin} onChange={e => setEditData(d => ({ ...d, origin: e.target.value }))} data-testid="edit-origin" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Stock</Label>
                                <Input type="number" value={editData.stock} onChange={e => setEditData(d => ({ ...d, stock: e.target.value }))} data-testid="edit-stock" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Qté minimale</Label>
                                <Input type="number" value={editData.min_order} onChange={e => setEditData(d => ({ ...d, min_order: e.target.value }))} data-testid="edit-min-order" />
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Saison début</Label>
                                <select value={editData.season_start} onChange={e => setEditData(d => ({ ...d, season_start: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-season-start">
                                    <option value="">Toute année</option>
                                    {MONTHS.map((m, i) => <option key={`start-${m}`} value={i + 1}>{m}</option>)}
                                </select>
                            </div>
                            <div>
                                <Label className="text-xs font-semibold">Saison fin</Label>
                                <select value={editData.season_end} onChange={e => setEditData(d => ({ ...d, season_end: e.target.value }))}
                                    className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="edit-season-end">
                                    <option value="">Toute année</option>
                                    {MONTHS.map((m, i) => <option key={`end-${m}`} value={i + 1}>{m}</option>)}
                                </select>
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditProduct(null)}>Annuler</Button>
                        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-product-btn">
                            {saving ? 'Enregistrement...' : 'Enregistrer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
